/**
 * Formats a dollar amount as a USD currency string (e.g. "$12.34").
 *
 * @param {number} amount - amount in dollars
 * @returns {string} USD-formatted string
 */
const toDollarDisplay = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);

/**
 * Formats an amount in cents as a USD currency string (e.g. "$12.34").
 *
 * @param {number} cents - amount in cents
 * @returns {string} USD-formatted string
 */
export const centsToDollarsDisplay = (cents: number): string => toDollarDisplay(Math.round(cents) / 100.0);

/**
 * Formats an amount in cents as a negative USD currency string (e.g. "-$12.34").
 *
 * @param {number} cents - amount in cents
 * @param {boolean} noWrap - use a non-breaking hyphen so the value can't wrap mid-amount in inline text
 * @returns {string} negative USD-formatted string
 */
export const centsToNegativeDollarsDisplay = (cents: number, noWrap = false): string => (cents === 0 ? '$0.00' : `${noWrap ? '\u2011' : '-'}${toDollarDisplay(Math.round(cents) / 100.0).replace('-', '')}`);

/**
 * Converts an amount in cents to dollars.
 *
 * @param {number} num - amount in cents
 * @returns {number} amount in dollars
 */
export const centsToDollars = (num: number): number => num / 100.0;

/**
 * Formats a numeric string representing an amount in cents into a USD currency string.
 *
 * @param amount - A string representing the monetary amount in cents.
 *                 Non-numeric characters will be stripped before conversion.
 *                 If the input is invalid or null/undefined, "$0.00" will be returned.
 *
 * @returns A string formatted as USD currency (e.g., "$12.34").
 */
export const formatCurrency = (amount: string | null | undefined): string => {
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Ensure `amount` is a valid string
  if (!amount) {
    return currencyFormatter.format(0); // Default to $0.00 for null, undefined, or empty input
  }

  // Remove all non-numeric characters, parse to integer, and handle invalid results
  const numericValue = parseInt(amount.replace(/\D/g, ''), 10);
  if (Number.isNaN(numericValue)) {
    return currencyFormatter.format(0); // Default to $0.00 for invalid input
  }

  // Convert cents to dollars and format as USD currency
  return currencyFormatter.format(numericValue / 100);
};
